import { ComponentStore } from '@ngrx/component-store';
import { HttpErrorResponse } from '@angular/common/http';
import { EventEmitter, inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { tapResponse } from '@ngrx/operators';
import { TransactionTypeService } from '../../services/transaction-type.service';
import { FormGroup } from '@angular/forms';
import { DownloadTemplateApiStore } from '../../api-stores/download-template.api-store';
import { ApiFailureText } from '@app/enum/alert-text.enum';
import { AddTransactionTypeForm } from './add-transaction-type.form';
import { PhoenixToastService } from '@kehe/phoenix-notifications';
import { TransactionTypeMediatorService } from '../../services/transaction-type-mediator.service';
import { Store } from '@ngrx/store';
import { toObservable } from '@angular/core/rxjs-interop';
import { CostCenterStore } from '@app/modules/supplier-deductions/cost-center/cost-center.store';

export interface AddTransactionTypeState {
  spendCategoryDataSource: any[];
  isUpdating?: boolean;
  isSaving?: boolean;
  hasError?: boolean;
  errorMessage?: string;
}

export const initialState: AddTransactionTypeState = {
  spendCategoryDataSource: [],
  isUpdating: false,
  isSaving: false,
  hasError: undefined,
  errorMessage: undefined,
};

@Injectable()
export class AddTransactionTypeStore extends ComponentStore<AddTransactionTypeState> {
  public closeModalEmitter = new EventEmitter<void>();
  formGroup: FormGroup;

  constructor() {
    super(initialState);
  }
  private readonly _transactionTypesApiStore = inject(TransactionTypeService);
  private toastService = inject(PhoenixToastService);
  private transactionMediator = inject(TransactionTypeMediatorService);
  private readonly _downloadTemplateApiStore = inject(DownloadTemplateApiStore);
  private readonly store = inject(Store);
  private readonly costCenterStore = inject(CostCenterStore);
  public addTransactionTypeForm = inject(AddTransactionTypeForm);

  readonly costCenters$ = toObservable(this.costCenterStore.costCenters);
  readonly costCentersLoading$ = toObservable(this.costCenterStore.isLoading);

  readonly vm$ = this.select(
    this.state$,
    this.costCenters$,
    this.costCentersLoading$,
    (state, costCenters, costCentersLoading) => ({
      ...state,
      costCenters,
      costCentersLoading,
    }),
  );

  readonly getSpendCategory$ = this.effect((params$: Observable<any>) =>
    params$.pipe(
      switchMap((_) =>
        this._transactionTypesApiStore.getSpendCategoryList().pipe(
          tapResponse({
            next: (response) => {
              this.patchState({
                spendCategoryDataSource: response,
              });
            },
            error: (_: HttpErrorResponse) => {
              throw new Error(ApiFailureText.Generic);
            },
          }),
        ),
      ),
    ),
  );

  saveTransactionType$ = this.effect<{ editData?: any | null }>((params$) => {
    return params$.pipe(
      switchMap((params) => {
        const editData = params?.editData;
        const isUpcIdentified =
          this.addTransactionTypeForm.form.value.isUpcIdentified;
        const payload = {
          transactionTypeDescription:
            this.addTransactionTypeForm.form.value.transactionTypeDescription,
          invoiceHeaderDescription:
            this.addTransactionTypeForm.form.value.invoiceHeaderDescription,
          invoicePrefix: this.addTransactionTypeForm.form.value.invoicePrefix,
          chain: this.addTransactionTypeForm.form.value.chain,
          costCenter: this.addTransactionTypeForm.form.value.costCenter,
          invoiceType: this.addTransactionTypeForm.form.value.invoiceType,
          spendCategoryId: this.addTransactionTypeForm.form.value.spendCategory,
          file: this.addTransactionTypeForm.form.value.fileUpload?.[0] ?? null,
          hasEpFee: this.addTransactionTypeForm.form.value.hasEpFee,
          lineIdentifier:
            isUpcIdentified === true
              ? 'Upc'
              : isUpcIdentified === false
                ? 'Esn'
                : editData.lineIdentifier,
          id: null,
        };

        payload['isHandKey'] = this.addTransactionTypeForm.form.value.isHandKey;

        const hasSupportingDocument =
          this.addTransactionTypeForm.form.value.hasSupportingDocument;
        if (hasSupportingDocument) {
          payload['supportingDocumentFile'] =
            this.addTransactionTypeForm.form.value.supportingDocumentFile[0];
        }
        payload['hasSupportingDocument'] = hasSupportingDocument;

        if (editData?.id) {
          payload.id = editData.id;
        }
        if (editData?.code && !payload.invoicePrefix) {
          payload.invoicePrefix = editData.code;
          payload['isHandKey'] = editData.isHandKey ?? false;
        }

        this.patchState({
          isSaving: true,
        });
        return this._transactionTypesApiStore.saveTransactionType(payload).pipe(
          tapResponse({
            next: (response) => {
              this.patchState({
                hasError: false,
                errorMessage: undefined,
                isSaving: false,
              });
              if (response?.id.length > 0) {
                this.closeModalEmitter.emit();

                if (editData?.id) {
                  this.toastService.showSuccessToast(
                    'Transaction Type updated successfully!',
                  );
                } else {
                  this.toastService.showSuccessToast(
                    'Transaction Type added successfully!',
                  );
                }

                this.transactionMediator.triggerReload();
              }
            },
            error: (err: HttpErrorResponse) => {
              this.patchState({
                hasError: true,
                errorMessage:
                  this.getErrorFromApiHtml(err.error) || 'An error occurred',
                isSaving: false,
              });
            },
          }),
        );
      }),
    );
  });

  getErrorFromApiHtml(error: any): string {
    if (error === undefined || error === null || error?.length === 0) {
      return '';
    }
    if (error instanceof ProgressEvent) {
      return `
      <div>
        <p class="mb-1">Unable to add your Transaction Type due to a network or file size error. Please check your connection or file size and try again.</p>
      </div>
    `;
    }
    return `
    <div>
      <p class="mb-1">Unable to add your Transaction Type due to the following reasons:</p>
      <ul class="ps-3">
        <li>
          ${error}
        </li>
      </ul>
    </div>
  `;
  }

  downloadTemplate(
    transactionTypeName: string,
    isEsnTemplate: boolean = false,
  ) {
    this._downloadTemplateApiStore.downloadTemplate$({
      transactionTypeName,
      isEsnTemplate,
    });
  }

  loadTemplate(code: string, onLoaded: (file: File) => void) {
    this._downloadTemplateApiStore.loadTemplateFile({
      code,
      onLoaded,
      onError: (e) => {
        this.patchState({
          hasError: true,
          errorMessage: ApiFailureText.Generic,
        });
      },
    });
  }

  loadSupportingDocument(code: string, onLoaded: (file: File) => void) {
    this._downloadTemplateApiStore.loadSupportingDocument({
      code,
      onLoaded,
      onError: (e) => {
        this.patchState({
          hasError: true,
          errorMessage: ApiFailureText.Generic,
        });
      },
    });
  }

  loadCostCenters(filter: string): void {
    this.costCenterStore.loadCostCenters(filter);
  }
}
